import {ClassValue} from "class-variance-authority/dist/types";
import clsx from "clsx";
import {twMerge} from 'tailwind-merge'
import {format} from "date-fns";
import {AxiosError} from "axios";
import {TShippingItem} from "features/shipping/types";
import {IShippingWithCluster} from "features/plan/types";

export const isBrowser = () => typeof window !== "undefined";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

export function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay()
}

export function createTimeOptions(length: number) {
  return Array.from({length}, (_, i) => {
    return ({
      value: i, label: String(i)
      .padStart(2, "0")
    })
  })
}

export function formatDate(date: Date | null, formatStr: string = "yyyy-MM-dd"): string {
  try {
    if (date) return format(date, formatStr);
    return ""
  } catch (e) {
    return ""
  }
}

export function objectToStringArray(o: any, arraySupported?: boolean): string[] {
  const keyValuePairs: string[] = [];
  Object.keys(o)
        .forEach((key) => {
          if (Array.isArray(o[key])) {
            if (arraySupported) {
              keyValuePairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(o[key].join(","))}`);
            }
            else {
              o[key].forEach((str: any) => {
                keyValuePairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(str)}`);
              });
            }
          }
          else {
            keyValuePairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(o[key])}`);
          }
        });
  return keyValuePairs;
}

export function objToQueryString(obj?: any, arraySupported?: boolean) {
  if (!obj) {
    return "";
  }
  let keyValuePairs: string[] = [];
  if (Array.isArray(obj)) {
    obj.forEach((o) => {
      keyValuePairs = [
        ...keyValuePairs, ...objectToStringArray(o, arraySupported),
      ];
    });
  }
  else {
    keyValuePairs = objectToStringArray(obj, arraySupported);
  }
  return keyValuePairs.join("&");
}

export function hexColorDelta(hex1: string, hex2: string) {
  // get red/green/blue int values of hex1
  var r1 = parseInt(hex1.substring(0, 2), 16);
  var g1 = parseInt(hex1.substring(2, 4), 16);
  var b1 = parseInt(hex1.substring(4, 6), 16);
  // get red/green/blue int values of hex2
  var r2 = parseInt(hex2.substring(0, 2), 16);
  var g2 = parseInt(hex2.substring(2, 4), 16);
  var b2 = parseInt(hex2.substring(4, 6), 16);
  // calculate differences between reds, greens and blues
  var r = 255 - Math.abs(r1 - r2);
  var g = 255 - Math.abs(g1 - g2);
  var b = 255 - Math.abs(b1 - b2);
  // limit differences between 0 and 1
  r /= 255;
  g /= 255;
  b /= 255;
  // 0 means opposite colors, 1 means same colors
  return (r + g + b) / 3;
}

const GAB = 0.6;  // 0 means opposite colors, 1 means same colors
export const getRandomNumber = (colorsToFilter?: string[]) => {
  let random = Math.floor(Math.random() * 16777215);
  
  let randomHex = random.toString(16);
  if (colorsToFilter) {
    let containSimilarColor = false
    for (let i in colorsToFilter) {
      const color = colorsToFilter[i];
      if (hexColorDelta(randomHex, color) > GAB) {
        containSimilarColor = true;
        break;
      }
    }
    if (containSimilarColor) randomHex = getRandomNumber(colorsToFilter);
  }
  return randomHex;
};

export const isKorean = (str) => {
  const regExp = /^[ㄱ-ㅎ|ㅏ-ㅣ|가-힣].*$/;
  if (regExp.test(str)) {
    return true;
  }
  return false;
};

export const isEnglish = (str) => {
  const regExp = /[a-zA-Z].+/;
  if (regExp.test(str)) {
    return true;
  }
  return false;
};

/**
 * Search params are held as an array of two elements arrays where member 0 is the key and member 1 is value
 * This function extracts the values from the array and returns an object with the key as the property name and the value as the property value
 * Where a key has multiple values, the value is an array of values
 *
 * @param {URLSearchParams} searchParams
 */
export function extractExistingParams<T = any>(searchParams: URLSearchParams): T {
  const entries = Array.from(searchParams.entries());
  return entries.reduce((acc: any, a) => {
    return ((acc[a[0]] = acc[a[0]] || []).push(a[1]), acc)
  }, {});
}

/**
 * Remove a value from an existing parameter where the parameter can occur multiple times
 * If the value is the last value, the parameter is removed
 *
 * @param {URLSearchParams} searchParams
 * @param {string} key
 * @param {string} value
 */
export function removeExistingParamsArrayValue(searchParams, key, value) {
  const existingParams = extractExistingParams(searchParams);
  if (existingParams[key]) {
    existingParams[key] = existingParams[key].filter(v => v !== value);
  }
  if (existingParams[key]?.length === 0) {
    delete existingParams[key];
  }
  return existingParams;
}

export function isUpdateDisabled(status?: string, is_scanned?: boolean) {
  if (status && status.startsWith("RETURN")) return !status.startsWith("RETURN_CREATED") && !status.startsWith("RETURN_WAITINGFORSORT");
  return is_scanned;
}

export const getApiErrorMsg = (err: AxiosError<any>) => {
  const errors: any[] = err.response?.data?.errors;
  let toast: string = "";
  if (errors && errors.length > 0) {
    errors.forEach((e) => {
      switch (e.code) {
        case "invalid":
          if (err.config?.url?.includes("containers/checkin")) {
            toast = "등록할 수 없는 박스 또는, 잘못된 UUID입니다. 다시 확인해주세요.";
          }
          else {
            toast = "잘못된 " + e.field + "입니다. 다시 확인해주세요.";
          }
      }
      if (!toast) {
        switch (e.field) {
          case "toast":
            toast = typeof e.message === "string" ? e.message : e.message?.message;
            return;
          case "ui.text":
            toast = typeof e.message === "string" ? e.message : e.message?.message;
            break;
          case "ui.title":
          case "ui.button.ok":
          case "ui.button.cancel":
            break;
          case "":
          default:
            toast = typeof e.message === "string" ? e.message : e.message?.message;
        }
      }
    });
  }
  else {
    toast = err?.response?.data?.detail || err.message;
  }
  return toast;
};

export function getTrackingList(items: any[], trackingKey: string) {
  const trackingList: string[] = [];
  items && items.forEach((item) => {
    trackingList.push(item[trackingKey]);
  });
  return trackingList;
}
